import React, { useEffect, useState } from 'react';
import { Download, Globe, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { transportApi, downloadCsv } from '@/services/transportApi';
import { IpLine, SelfieThumb } from '@/components/transport/attendance/PunchBits';

const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }); } catch { return '—'; } };
const NoGps = ({ testId }) => <Badge className="mt-0.5 bg-red-100 text-red-700 text-[10px]" data-testid={testId}>No GPS</Badge>;
const Loc = ({ text, testId }) => text ? <div className="text-[11px] text-gray-500 flex items-center gap-1" data-testid={testId}><MapPin className="w-3 h-3" />{text}</div> : null;

const ExportBar = ({ date }) => {
  const [from, setFrom] = useState(`${date.slice(0, 8)}01`);
  const [to, setTo] = useState(date);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFrom(`${date.slice(0, 8)}01`); setTo(date); }, [date]);
  const run = async () => {
    if (!from || !to || from > to) { toast.error('Pick a valid From / To range'); return; }
    setBusy(true);
    try { await downloadCsv(`/transport/attendance/export?date_from=${from}&date_to=${to}`, `driver_attendance_${from}_to_${to}.csv`); }
    catch { toast.error('Export failed — up to 92 days at a time'); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 ml-auto" data-testid="attendance-export-bar">
      <span className="text-sm text-gray-600">Export</span>
      <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[150px]" data-testid="attendance-export-from" />
      <span className="text-gray-400">→</span>
      <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[150px]" data-testid="attendance-export-to" />
      <Button onClick={run} disabled={busy} variant="outline" className="border-amber-400 text-amber-700" data-testid="attendance-export-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download CSV
      </Button>
    </div>
  );
};

const PunchCell = ({ s, p, did, i }) => (
  <div className="flex items-start gap-2">
    <SelfieThumb url={s[`${p}_selfie_url`]} label={`${p === 'checkin' ? 'Check-In' : 'Check-Out'} selfie · ${fmt(s[`${p}_time`])}`} testId={`attendance-${p}-selfie-${did}-${i}`} />
    <div className="min-w-0">
      <div>{fmt(s[`${p}_time`])}</div>
      <Loc text={s[`${p}_location`]} testId={`attendance-${p}-loc-${did}-${i}`} />
      {(`${p}_ip` in s) && !(p === 'checkout' && s.auto_closed) && <IpLine ip={s[`${p}_ip`]} network={s[`${p}_network`]} office={s[`${p}_office`]} testId={`attendance-${p}-ip-${did}-${i}`} />}
    </div>
  </div>
);

export const DailyAttendance = () => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  useEffect(() => { transportApi.listAttendance({ date }).then(setRows).catch(() => toast.error('Failed to load attendance')); }, [date]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600">Date</label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="max-w-[180px]" data-testid="attendance-date-input" />
        <ExportBar date={date} />
      </div>
      <Card className="border-0 shadow-md"><CardContent className="p-0">
        <Table data-testid="attendance-table">
          <TableHeader><TableRow>
            <TableHead>Driver</TableHead><TableHead>Check-In</TableHead><TableHead>Check-Out</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.flatMap((r) => {
              const sessions = (r.sessions && r.sessions.length) ? r.sessions : [{ checkin_time: r.checkin_time, checkin_location: r.checkin_location, checkout_time: r.checkout_time, checkout_location: r.checkout_location }];
              return sessions.map((s, i) => (
                <TableRow key={`${r.id}-${i}`} data-testid={`attendance-row-${r.driver_id}${i > 0 ? `-${i}` : ''}`}>
                  <TableCell className="font-semibold">
                    {i === 0 ? r.driver_name : <span className="text-gray-400 text-xs pl-2">↳ {r.driver_name}</span>}
                    {s.reopened && <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]" data-testid={`attendance-reopened-${r.driver_id}-${i}`}>re-check-in</Badge>}
                  </TableCell>
                  <TableCell>
                    <PunchCell s={s} p="checkin" did={r.driver_id} i={i} />
                    {s.checkin_time && !s.checkin_gps && <NoGps testId={`attendance-checkin-nogps-${r.driver_id}-${i}`} />}
                    {s.approved_by_name && <div className="text-[10px] text-amber-600">approved by {s.approved_by_name}</div>}
                  </TableCell>
                  <TableCell>
                    <PunchCell s={s} p="checkout" did={r.driver_id} i={i} />
                    {s.auto_closed && <div className="text-[11px] text-gray-500 flex items-center gap-1" data-testid={`attendance-checkout-ip-auto-${r.driver_id}-${i}`}><Globe className="w-3 h-3" />Auto (12 AM)</div>}
                    {s.auto_closed && <Badge className="mt-0.5 bg-indigo-100 text-indigo-700 text-[10px]" data-testid={`attendance-autoclosed-${r.driver_id}-${i}`}>Auto-closed 12 AM</Badge>}
                    {s.needs_hr && <Badge className="mt-0.5 ml-1 bg-red-100 text-red-700 text-[10px]" data-testid={`attendance-needs-hr-${r.driver_id}-${i}`}>Contact HR</Badge>}
                    {s.missed_checkout && !s.hr_fixed && <Badge className="mt-0.5 bg-orange-100 text-orange-700 text-[10px]" data-testid={`attendance-missed-${r.driver_id}-${i}`}>Missed check-out — HR to enter</Badge>}
                    {s.hr_fixed && <div className="text-[11px] text-gray-600 mt-0.5" data-testid={`attendance-hr-fixed-${r.driver_id}-${i}`}>Entered by HR{s.hr_fixed_by ? ` (${s.hr_fixed_by})` : ''}{s.hr_reason ? ` — ${s.hr_reason}` : ''}</div>}
                    {s.checkout_time && !s.checkout_gps && !s.auto_closed && !s.hr_fixed && <NoGps testId={`attendance-checkout-nogps-${r.driver_id}-${i}`} />}
                  </TableCell>
                  <TableCell>{s.checkout_time ? <Badge variant="secondary">Done</Badge> : s.missed_checkout ? <Badge className="bg-orange-100 text-orange-700">Missed check-out</Badge> : s.checkin_time ? <Badge className="bg-green-100 text-green-700">On duty</Badge> : <Badge variant="outline">Absent</Badge>}</TableCell>
                </TableRow>
              ));
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-8" data-testid="attendance-empty">No attendance for this date.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
};

export default DailyAttendance;
